export type ReviewFilterTab<T extends string> = {
  id: T;
  label: string;
  dot?: string;
  count?: number;
};

type ReviewsFilterTabsProps<T extends string> = {
  tabs: ReviewFilterTab<T>[];
  value: T;
  onChange: (id: T) => void;
  ariaLabel?: string;
};

/**
 * Trioty review status filter pills (dot + label + count). Mirrors the shared
 * SegmentedTabs look, kept local so the reviews toolbar can tune spacing to the
 * design.
 */
export function ReviewsFilterTabs<T extends string>({
  tabs,
  value,
  onChange,
  ariaLabel,
}: ReviewsFilterTabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="no-scrollbar flex items-center gap-1 overflow-x-auto rounded-[13px] border border-[color:var(--line2)] bg-[color:var(--surface-alt)] p-1"
    >
      {tabs.map((tab) => {
        const active = tab.id === value;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.id)}
            className={[
              "inline-flex shrink-0 items-center gap-[7px] rounded-[9px] px-[13px] py-[7px] text-[13px] transition-colors",
              active
                ? "bg-[color:var(--card)] font-semibold text-[color:var(--text-primary)] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                : "font-medium text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]",
            ].join(" ")}
          >
            {tab.dot ? (
              <span
                aria-hidden="true"
                className="h-[7px] w-[7px] rounded-full"
                style={{ background: tab.dot }}
              />
            ) : null}
            {tab.label}
            {tab.count !== undefined ? (
              <span
                className={[
                  "text-[11px] font-bold",
                  "text-[color:var(--text-muted)]",
                ].join(" ")}
              >
                {tab.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
